import { createApi } from '@reduxjs/toolkit/query/react';
import { endPoints, urlKeys } from 'src/api/APIEndpoints';
import { ISIVChecklist } from 'src/types/SIVChecklist';
import { formatString } from 'src/util/STR';
import { baseQuery } from './baseQuery';

export const sivChecklistApi = createApi({
  baseQuery,
  reducerPath: 'sivChecklistApi',
  endpoints: builder => ({
    getSivChecklist: builder.query<
      ISIVChecklist,
      {
        monitoring_visit_id: string | null;
        site_id: string | null;
        study_id: string | null;
        country: string | null;
        application: string | undefined;
      }
    >({
      query: ({ monitoring_visit_id, site_id, study_id, country, application }) => ({
        url: formatString(
          endPoints.SivChecklist.select.url,
          { key: urlKeys.monitoring_visit_id, value: monitoring_visit_id },
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        )
      }),
      extraOptions: { maxRetries: 3 },
      keepUnusedDataFor: endPoints.SivChecklist.select.keepUnusedDataFor
    }),
    updateSivChecklist: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, application, data }) => ({
        url: formatString(
          endPoints.Site.update,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.country, value: country },
          { key: urlKeys.application, value: application }
        ),
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    signSivChecklist: builder.mutation<any, any>({
      query: ({ site_id, study_id, reason, username, password, country, application, data }) => ({
        url: `${formatString(
          endPoints.SivChecklist.signSivChecklist,
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.site_id, value: site_id }
        )}?reason=${reason}&username=${username}&password=${password}&country=${country}&application=${application}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getAllSIVChecklists: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, application, application_section, application_module_section }) => ({
        url: `${formatString(
          endPoints.SivChecklist.getAll
        )}?study_id=${study_id}&site_id=${site_id}&country=${country}&application=${application}&application_section=${application_section}&application_module_section=${application_module_section}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),

    publishSIVChecklistToEtmf: builder.mutation<any, any>({
      query: ({
        site_id,
        study_id,
        country,
        publish_doc_version,
        is_published,
        monitoring_visit_id,
        update,
        application,
        application_section
      }) => ({
        url: `${formatString(
          endPoints.SivChecklist.pulishEtmf,
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.site_id, value: site_id }
        )}?country=${country}&publish_doc_version=${publish_doc_version}&monitoring_visit_id=${monitoring_visit_id}&is_published=${is_published}&update=${update}&application=${application}&application_section=${application_section}`,
        method: 'POST',
        body: {}
      })
    }),

    sendForReview: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, data, application, monitoring_visit_id }) => ({
        url: `${formatString(
          endPoints.SivChecklist.notifyUser,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&monitoring_visit_id=${monitoring_visit_id}&application_section=CONDUCT&application_module_section=IMV%20REPORT`,
        method: 'PUT',
        body: data
      })
    })
  })
});

export const {
  useGetSivChecklistQuery,
  useLazyGetSivChecklistQuery,
  useSignSivChecklistMutation,
  useUpdateSivChecklistMutation,
  useGetAllSIVChecklistsMutation,
  usePublishSIVChecklistToEtmfMutation,
  useSendForReviewMutation
} = sivChecklistApi;
